// @flow
import * as React from 'react';
import { Box } from 'grommet';
import type { LeafletContext } from 'react-leaflet/lib/types';
import { Route } from 'react-router-dom';
import { withLeaflet } from 'react-leaflet';
import { injectIntl, IntlShape } from 'react-intl';
import {
    isEqual, merge, isEmpty, cloneDeep,
} from 'lodash';
import UseHighlightPoint from 'esm-web-pro/hooks/useHighlightPoint';
import HorizontalLine from 'esm-web-pro/components/layout/HorizontalLine';
import UpperContainer from 'esm-web-pro/components/layout/sidePanel/UpperContainer';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import NavigateBackButton from 'esm-web-pro/components/maps/NavigateBackButton';
import { point12Messages } from 'esm-web-pro/messages/points12Messages';
import { numberPointsCallbacksCable } from 'esm-web-pro/containers/pages/network/objects/cable/MapCable';
import CouplerIcon from 'esm-web-pro/components/layout/sidePanel/icons/CouplerIcon';
import TerminalBoxIcon from 'esm-web-pro/components/layout/sidePanel/icons/TerminalBoxIcon';
import ODFIcon from 'esm-web-pro/components/layout/sidePanel/icons/ODFIcon';
import SwitchIcon from 'esm-web-pro/components/layout/sidePanel/icons/SwitchIcon';
import { connectAPI, withRouter } from 'speed-js-react';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import GisPointIcon from 'esm-web-pro/components/layout/sidePanel/icons/GisPointIcon';
import UseHighlightCable from 'esm-web-pro/containers/pages/network/objects/cable/useHighlightCable';
import useHighlightFiber from 'esm-web-pro/containers/pages/network/objects/fiber/useHighlightFiber';
import SidePane from 'esm-web-pro/components/layout/SidePane';
import ModelAttributes from 'esm-web-pro/containers/pages/network/ModelAttributes';
import usePaginateData from 'esm-web-pro/hooks/usePaginateData';
import { ClickedRowContext } from 'esm-web-pro/containers/pages/network/objects/point12/ClickedRowContext';
import BottomContainer from 'esm-web-pro/components/layout/sidePanel/BottomContainer';
import Point12Actions from './Point12Actions';
import Point12Attributes from './Point12Attributes';
import Point12Content from './Point12Content';


type Props = {
    panelId: String,
    object: Object,
    map: Object,
    updateObject: Function,
    editor: BaseEditor,
    history: Object,
    match: Object,
    resource: Object,
    objectLayer: Object,
    leaflet: LeafletContext,
    getCables: Function,
    getSplices: Function,
    getOpticalODFPorts: Function,
    intl: IntlShape,
    cables: ?{
        cables: Object,
        limit: number,
        offset: number,
        next: ?boolean,
    },
    cablesError: ?string,
    splices: ?{
        splices: Object,
        limit: number,
        offset: number,
        next: ?string,
    },
    opticalODFPorts: ?{
        splices: Object,
        limit: number,
        offset: number,
        next: ?string,
    },
    data_ports: Object[],
}


const Point12 = (props: Props) => {
    const {
        object: point, resource, map: networkMap, editor, updateObject, history, match, objectLayer, leaflet: { map },
        intl: { formatMessage }, getCables, getSplices, getOpticalODFPorts, cables, splices, cablesError,
        opticalODFPorts, data_ports: dataPorts, panelId,
    } = props;
    const [clickedRowIdentity, setClickedRowIdentity] = React.useState(null);
    const [focusCableCallbacks, setFocusCableCallbacks] = React.useState([]);
    const [paths, setPaths] = React.useState({});

    const [allCables, setAllCables] = React.useState({});

    const [allSplices, moreSplices, resetSplices] = usePaginateData(splices, getSplices,
        [point.uuid, networkMap], () => getCables(point.uuid, networkMap, 10, 0));
    const [allOpticalODFPorts, moreOpticalODFPorts, resetOpticalODFPorts] = usePaginateData(opticalODFPorts,
        getOpticalODFPorts, [point.uuid, networkMap], () => getCables(point.uuid, networkMap, 10, 0));

    const pointCoords = [...point.container.coordinates.coordinates].reverse();

    const focusPoint = React.useCallback(UseHighlightPoint(
        point.container.coordinates.coordinates,
        point.container.type === 'pop' && point.container.alias
            ? point.container.alias
            : point.container.sequence,
    ), [point]);

    const [highlightFiberFunction, highlightFiberCleanUp, clearHighlightedFibers] = useHighlightFiber();

    const [loading, setLoading] = React.useState(false);

    const getCableStartAndEndPoints = React.useCallback(() => {
        if (!isEmpty(point.cable_starts)) {
            point.cable_starts.forEach((cable) => editor
                .registerMultipleLoadingCallbacks(numberPointsCallbacksCable(cable)));
        }
        if (!isEmpty(point.cable_ends)) {
            point.cable_ends.forEach((cable) => editor
                .registerMultipleLoadingCallbacks(numberPointsCallbacksCable(cable)));
        }
    }, [point, editor]);

    const handleReload = () => {
        if (point.type === 'coupler' || point.type === 'terminalbox') {
            resetSplices();
        } else if (point.type === 'odf') {
            resetOpticalODFPorts();
        }
        loadObjects();
    };

    const loadObjects = () => {
        if (point.type === 'coupler' || point.type === 'terminalbox') {
            getSplices(point.uuid, networkMap, 10, 0);
        } else if (point.type === 'odf') {
            getOpticalODFPorts(point.uuid, networkMap, 10, 0);
        } else if (point.type === 'switch') {
            setLoading(true);
            getCables(point.uuid, networkMap).then(() => setLoading(false));
        }
    };

    React.useEffect(() => {
        loadObjects();
        return () => {
            editor.unregisterAllLoadingCallback();
            highlightFiberCleanUp();
        };
    }, []);

    React.useEffect(() => {
        if (cables?.cables) {
            setAllCables({ ...allCables, ...cables.cables });
        }

        if (cables?.next) {
            getCables(point.uuid, networkMap, cables.limit, cables.offset + cables.limit);
        }
    }, [cables]);


    React.useEffect(() => {
        const pointCables = [...point.cable_starts, ...point.cable_ends];
        const cableCallbacks = [];
        const cablePaths = {};
        const pointCoordinates = [...point.container.coordinates.coordinates].reverse();
        if (map) {
            map.highlightedGeometry = {
                bounds: null, center: pointCoordinates,
            };
            map.panTo(pointCoordinates);
        }

        Object.keys(pointCables).forEach((cableId) => {
            cablePaths[cableId] = pointCables[cableId].path.map((key2) => [key2[1], key2[0]]);
        });

        setPaths(cablePaths);

        const keys = Object.keys(cablePaths);
        keys.forEach((key) => {
            let path = cablePaths[key];

            if (!isEqual(pointCoordinates, path[0])) {
                path = path.reverse();
            }

            const focusCableCallback = UseHighlightCable(path);
            cableCallbacks.push(focusCableCallback);
        });
        editor.registerLoadingCallback(focusPoint);
        getCableStartAndEndPoints();
        cableCallbacks.forEach((callback) => {
            editor.registerLoadingCallback(callback);
        });
        setFocusCableCallbacks(cableCallbacks);
        editor.reloadFeatures();
    }, [getCableStartAndEndPoints, point, setFocusCableCallbacks, focusPoint, setPaths, editor, map]);

    const handleView = React.useCallback(
        (e: SyntheticMouseEvent<HTMLInputElement>, callbacks = null, bounds = null) => {
            editor.unregisterAllLoadingCallback();
            clearHighlightedFibers();
            if (callbacks) {
                callbacks.forEach((callback) => editor.registerLoadingCallback(callback));
            } else {
                getCableStartAndEndPoints();
                editor.registerLoadingCallback(focusPoint);
                setClickedRowIdentity(null);
                focusCableCallbacks.forEach((callback) => editor.registerLoadingCallback(callback));
            }
            if (map) {
                if (bounds) {
                    map.highlightedGeometry = {
                        bounds, center: pointCoords,
                    };
                    map.flyToBoundsCenter(bounds, pointCoords);
                } else {
                    map.highlightedGeometry = {
                        bounds: merge(...Object.values(cloneDeep(paths))), center: pointCoords,
                    };
                    map.flyToBoundsCenter(merge(...Object.values(cloneDeep(paths))), pointCoords);
                }
            }
            editor.reloadFeatures();
        }, [
            editor, map, getCableStartAndEndPoints, setClickedRowIdentity,
            focusCableCallbacks, clearHighlightedFibers, paths,
        ],
    );

    const setIcon = () => {
        if (point.type === 'coupler') {
            return <CouplerIcon />;
        } if (point.type === 'terminalbox') {
            return <TerminalBoxIcon />;
        } if (point.type === 'odf') {
            return <ODFIcon />;
        } if (point.type === 'switch') {
            return <SwitchIcon />;
        }
        return <GisPointIcon />;
    };

    return (
        <ClickedRowContext.Provider value={[clickedRowIdentity, setClickedRowIdentity]}>
            <SidePane
                id={panelId}
                openInitially
                closeAllOpened
                title={point.sequence}
                titleLabel={
                    point.type === 'switch' ? point.oss_switch_name : formatMessage(point12Messages[point.type])}
                titleIcon={setIcon()}
                titleOnView={handleView}
                actions={(
                    <NavigateBackButton panelId={panelId} />
                )}
            >
                <Box height="100%" justify="between">
                    <Box flex>
                        <UpperContainer>
                            <HorizontalLine color="grey" />
                            <Point12Attributes point={point} match={match} onView={handleView} />
                            <HorizontalLine color="grey" />
                            <Box direction="row">
                                <Point12Actions
                                    point={point}
                                    cables={allCables}
                                    getCables={getCables}
                                    objectLayer={objectLayer}
                                    map={networkMap}
                                    onEdit={updateObject}
                                    resource={resource}
                                    editor={editor}
                                    history={history}
                                    match={match}
                                    updateObject={updateObject}
                                    setLoadingCallback={setLoading}
                                    onView={(...args) => {
                                        setClickedRowIdentity(null);
                                        return handleView(args);
                                    }}
                                />
                            </Box>
                            <HorizontalLine color="grey" />
                        </UpperContainer>
                        <BottomContainer background="white">
                            {cablesError || (
                                <Point12Content
                                    point={point}
                                    loading={loading}
                                    setLoadingCallback={setLoading}
                                    cables={{ cables: allCables, more: cables?.next }}
                                    splices={{ splices: allSplices, more: moreSplices }}
                                    opticalODFPorts={{ opticalODFPorts: allOpticalODFPorts, more: moreOpticalODFPorts }}
                                    dataPorts={dataPorts}
                                    match={match}
                                    map={networkMap}
                                    onView={handleView}
                                    editor={editor}
                                    clickedRowIdentity={clickedRowIdentity}
                                    setClickedRowIdentity={setClickedRowIdentity}
                                    getCables={getCables}
                                    highlightFiber={highlightFiberFunction}
                                    highlightFiberCleanUp={highlightFiberCleanUp}
                                    clearHighlightedFibers={clearHighlightedFibers}
                                    onReload={handleReload}
                                />
                            )}
                        </BottomContainer>
                    </Box>

                    <Route
                        path={`${match.path}/model/:modelCode`.replace('//', '/')}
                        render={({ match: _match }) => (
                            <ModelAttributes url={_match.url} />
                        )}
                    />
                </Box>
            </SidePane>
        </ClickedRowContext.Provider>
    );
};

const apiCallback = (apiClient: ApiClient, props: Props) => ({
    getCables: {
        send: (id: string, map: Object, limit?: number, offset?: number) => {
            const query = {};
            if (limit && offset) {
                query.limit = limit;
                query.offset = offset;
            }
            if (map.is_draft) {
                query.is_draft = map.uuid;
            }
            return apiClient.resources[props.resource].cables(id, query);
        },
        success: (res) => ({
            cables: res.cables,
            data_ports: res.data_ports,
        }),
        error: (res) => ({ cablesError: res.error }),
    },
    getSplices: {
        send: (uuid: string, map: Object, limit: number, offset: number) => {
            const query = {
                container: uuid, limit, offset,
            };
            if (map.is_draft) {
                query.is_draft = map.uuid;
            }
            return apiClient.resources.splice.forPoint(query);
        },
        success: (res) => ({
            splices: {
                data: res.data.results,
                limit: res.data.limit,
                offset: res.data.offset,
                next: res.data.next,
            },
        }),
    },
    getOpticalODFPorts: {
        send: (uuid: string, map: Object, limit: number, offset: number) => {
            const query = {
                container: uuid, limit, offset,
            };
            if (map.is_draft) {
                query.is_draft = map.uuid;
            }
            return apiClient.resources.opticalodfport.forPoint(query);
        },
        success: (res) => ({
            opticalODFPorts: {
                data: res.data.results,
                limit: res.data.limit,
                offset: res.data.offset,
                next: res.data.next,
            },
        }),
    },
});

export default connectAPI(apiCallback)(withRouter(withLeaflet(injectIntl(Point12))));
